// doctor-frontend/src/pages/owner/SubjectDetail.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardActionArea, CardContent, Typography, Grid,
  Divider, Stack, TextField, CircularProgress, Alert
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { findSubjectBySlug } from '../../constants/subjects';
import http from '../../services/http';
import OwnerTopNav from '../../components/owner/OwnerTopNav';
import OwnerBrandFooter from '../../components/owner/OwnerBrandFooter';

const ALLOWED_INTERN_TENANTS = ['aiimsjodhpur-cmfm', 'gomco-patiala'];

function ModeCard({ label, sublabel, onClick, disabled }) {
  return (
    <Card sx={{ height: '100%', opacity: disabled ? 0.6 : 1, bgcolor: 'background.paper' }} variant="outlined">
      <CardActionArea onClick={onClick} disabled={disabled} sx={{ height: '100%', cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{label}</Typography>
          <Typography variant="caption" color="text.secondary">{sublabel}</Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function openTenantInNewTab(slug) {
  const url = `/t/${encodeURIComponent(slug)}/cmfm-login`;
  window.open(url, '_blank', 'noopener');
}

export default function SubjectDetail() {
  const { subjectSlug } = useParams();
  const subject = findSubjectBySlug(subjectSlug);

  const [mode, setMode] = useState(null);
  const [colleges, setColleges] = useState(null);
  const [filter, setFilter] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { setMode(null); setErr(''); setColleges(null); }, [subjectSlug]);

  useEffect(() => {
    if (!mode) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await http.get('/api/owner/tenants');
        if (!alive) return;
        setColleges(data?.tenants || []);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e?.message || 'Failed to load institutions');
      }
    })();
    return () => { alive = false; };
  }, [mode]);

  const showIntern = !!subject?.allowsIntern;
  const filterText = filter.trim().toLowerCase();

  const canOpenInternFor = useMemo(
    () => (slug) => ALLOWED_INTERN_TENANTS.includes(String(slug || '').toLowerCase()),
    []
  );

  if (!subject) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
        <OwnerTopNav active="subjects" />
        <Box p={3}><Alert severity="error">Subject not found.</Alert></Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      <OwnerTopNav active="subjects" />
      <Box p={3}>
        <Typography variant="h6" sx={{ mb: 1 }}>{subject.name}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Undergraduate and postgraduate modules are planned. Internship access is currently enabled only for selected institutional deployments.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}><ModeCard label="Undergraduate Module" sublabel="UG — Coming soon" onClick={() => setMode('ug')} /></Grid>
          <Grid item xs={12} sm={6} md={4}><ModeCard label="Postgraduate Module" sublabel="PG — Coming soon" onClick={() => setMode('pg')} /></Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ModeCard
              label="Intern Module"
              sublabel={showIntern ? 'Select an institution' : 'Not applicable'}
              onClick={() => showIntern && setMode('intern')}
              disabled={!showIntern}
            />
          </Grid>
        </Grid>

        {mode && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Choose an institution</Typography>
              <TextField size="small" placeholder="Search institution…" value={filter} onChange={(e) => setFilter(e.target.value)} />
            </Stack>

            {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
            {!colleges && !err && <CircularProgress size={20} />}

            {colleges && (
              <Grid container spacing={2}>
                {colleges
                  .filter(col => !filterText || (col.name || '').toLowerCase().includes(filterText) || (col.slug || '').toLowerCase().includes(filterText))
                  .map(col => {
                    const enabled = canOpenInternFor(col.slug);
                    return (
                      <Grid key={col.slug} item xs={12} sm={6} md={4} lg={3}>
                        <Card variant="outlined" sx={{ bgcolor: 'background.paper', opacity: enabled ? 1 : 0.6 }}>
                          <CardContent>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{col.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{col.slug}</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={!enabled}
                                onClick={(e) => { e.preventDefault(); if (enabled) openTenantInNewTab(col.slug); }}
                              >
                                {enabled ? 'Open Intern Module (new tab)' : 'Coming soon'}
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>
            )}
          </>
        )}
        <OwnerBrandFooter />
      </Box>
    </Box>
  );
}
